import { css } from '@emotion/css';
import { multiSelectStyle } from 'Shared/forms/Inputs'
import { Wrap, labelStyle, noteStyle } from 'Shared/forms/Wrap'
import type {StylesLayout} from 'Shared/model/Components';
import { optionList } from 'Shared/backend/widgets/ComponentOptions';
import { useBuild, useSite } from 'Shared/SiteProvider';
import {VenueSettingsFactory} from 'Shared/VenueSettingsFactory';
import { useVenue } from 'Shared/VenueProvider';
import { For, Show, createMemo, createSignal, onMount, onCleanup } from 'solid-js';
import { IntegratedSelectorAndComponent } from 'Shared/backend/widgets/IntegratedSelectorAndComponent';
import { Dynamic } from 'solid-js/web';
import { DragDropProvider, DragDropSensors, SortableProvider, createSortable, closestCenter, transformStyle, type DragEventHandler } from '@thisbeyond/solid-dnd';
import { initImage } from 'Shared/backend/widgets/CreationUtils';
import { StoreProvider, useStore } from 'Shared/StoreProvider';
import { dbPatchField } from 'Shared/forms/BaseOperations';
import { replaceField } from 'Shared/forms/SubdocOps';
import { useActiveStyles, ActiveStylesProvider } from 'Shared/backend/ActiveStylesContext';
import { settingsRulesCategory, NestedStyles } from 'Shared/frontend/styles/standardStyles';
import { SelectorSlot } from 'Shared/backend/styles/StyleFieldsRow';


export function initStyles(old?:any)
{
	return {
		...(old ?? {content: initImage()}),
		layout: 'styles'
	};
}

/*
	NOTE
	Style components could be viewed as roughly representing a kind of implicit "widget", and
	it may help to view them as such. The styles are often going to be reusable.

	While widgets could be formally separated out for reuse, this probably doesn't work... different
	widgets need to contain different content (eg different text, different images) which is easier accessed in situ.
	Ultimately this is what the facades would be for. The proposal is to apply facades at the section level - otherwise deciding
	what is in a widget is likely to be rather arbitrary.
	Also just selecting between prebaked sections is easiest for users.

	NOTE while styles could just be applied at the section level, this is probably not fine-grain enough.
	For example, consider a slider with overlays that are styled differently.

	NOTE while styles could be applied directly to components, this has its problems.
	Firsly the user is presented with many opportunties to apply styling, which we don't want - we want them to use themes and use defaults.
	Secondly it may appear as if just the component itself it being styled, where in truth the whole subtree is.

	TODO probably remove the Text component classes
*/

export function Styles(P:StylesLayout & {isTop?:boolean})
{
	const sw = useStore();

	const siteTheme = (new VenueSettingsFactory(useBuild())).createSettings(useSite().key,useVenue().settings).siteTheme();
	const options = optionList([],['popup']);

	const styleDefinitions = () => siteTheme.styles;

	//TODO create parallax class (apply here? or in overlay? or image? Dont know which image, so probably needs to be in overlay)

	//TODO add ignore page margins option

	//TODO add full phone margins (probably apply to Image component directly? Need to only affect images that haven't changed their margins)


	const detailsStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '0.8em',
	});

	/* Compute which styles are active from ancestors, and which will be active for our children. */
	const parentActiveStyles = useActiveStyles();

	const storePos = () => {
		let pos: any = sw.store;
		for (const part of (sw.location ?? [])) pos = pos?.[part];
		return pos;
	};

	onMount(async () => {
		if (!storePos()?.height)
			await replaceField(sw, 'height', P.isTop ? {child:true} : {parent:true});
	});

	const ownRemoveStyles = createMemo(() => (storePos()?.removeStyles ?? []) as {category:string,type:string}[]);
	const ownStyleRefs = createMemo(() =>
		((storePos()?.styles ?? []) as any[]).map(s => ({ category: s.category, type: s.type ?? s }))
	);

	/* Active styles we will pass down to our children. */
	const ownActiveStyles = createMemo(() => [
		...parentActiveStyles().filter(s => !ownRemoveStyles().some(r => r.category === s.category && r.type === s.type)),
		...ownStyleRefs(),
	]);


	return (
		<div class={detailsStyle}>
			<div class={noteStyle()}>
				This layout allows a component to be wrapped in space and borders, allows filters to be applied it, etc.
			</div>

			{/* Label above, not on the left: StylesField stacks the selector and each style's
			    parameter editor vertically, so it needs the full width. */}
			<Wrap label='Styles'>
				<StylesField styleDefinitions={styleDefinitions()} />
			</Wrap>

			<ActiveStylesProvider activeStyles={ownActiveStyles}>
				<IntegratedSelectorAndComponent {...P.content} label='Component' contentFieldName='content' options={options} required />
			</ActiveStylesProvider>


			{/* TODO
				Add a "custom style editor".
				Allow a "selection tree" to be auto-generated.
				Allow nodes to be styled.
				SEE StyleEditor.tsx for existing code !!! See additonal notes there!!
			*/}
		</div>
	);
}

/* Every category the venue can turn a style on from. The settings-page rule lists ride in the
   catalogue as a category so ShowWrapElement can resolve them, but they are one venue's settings
   rather than styles anyone picks - see settingsRulesCategory. */
function pickableCategories(styleDefinitions:NestedStyles): string[]
{
	return Object.keys(styleDefinitions).filter(category => category != settingsRulesCategory);
}


export function StylesField(P: { styleDefinitions: NestedStyles, field?: string })
{
	const sw = useStore();
	const field = P.field ?? 'styles';

	/* Navigate sw.store directly — getValue() calls useStore() internally which only works in
	   reactive contexts, not async event handlers. */
	const storePos = () => {
		let pos: any = sw.store;
		for (const part of (sw.location ?? []))
			pos = pos?.[part];
		return pos;
	};

	const value = createMemo(() => {
		const raw = (storePos()?.[field] ?? []) as any[];
		return raw
			.filter(item => P.styleDefinitions[item.category]?.[item.type])
			.map(item => ({ category: item.category, type: item.type }));
	});

	const isSelected = (category: string, styleName: string) =>
		value().some(v => v.category === category && v.type === styleName);

	const currentRaw = () => (storePos()?.[field] ?? []) as any[];

	const toggleStyle = async (category: string, styleName: string, e: MouseEvent) => {
		e.stopPropagation();
		const current = currentRaw();
		const newStyles = isSelected(category, styleName)
			? current.filter((s: any) => !(s.type === styleName && s.category === category))
			: [...current, {
				category,
				type: styleName,
				params: P.styleDefinitions[category]?.[styleName]?.initial ?? {}
			  }];
		await dbPatchField(sw, field, newStyles);
	};

	const removeStyle = async (category: string, styleName: string, e: MouseEvent) => {
		e.stopPropagation();
		const current = currentRaw();
		const newStyles = current.filter((s: any) => !(s.type === styleName && s.category === category));
		await dbPatchField(sw, field, newStyles);
	};

	const onDragEnd: DragEventHandler = async ({ draggable, droppable }) => {
		if (!draggable || !droppable || draggable.id === droppable.id) return;
		const shown = value();
		const fromEntry = shown[Number(draggable.id) - 1];
		const toEntry = shown[Number(droppable.id) - 1];
		const raw = currentRaw();
		const from = raw.findIndex((s: any) => s.category === fromEntry.category && s.type === fromEntry.type);
		const to = raw.findIndex((s: any) => s.category === toEntry.category && s.type === toEntry.type);
		if (from < 0 || to < 0 || from === to) return;
		const reordered = raw.slice();
		reordered.splice(to, 0, ...reordered.splice(from, 1));
		await dbPatchField(sw, field, reordered);
	};

	const [isOpen, setIsOpen] = createSignal(false);
	const [drilled, setDrilled] = createSignal<string | undefined>(undefined);
	const [tooltip, setTooltip] = createSignal<{category: string, name: string, top: number, left: number} | null>(null);

	const close = () => {
		setIsOpen(false);
		setDrilled(undefined);
	};

	let containerRef: HTMLDivElement;

	onMount(() => {
		const handler = (e: MouseEvent) => {
			if (!containerRef?.contains(e.target as Node))
				close();
		};
		document.addEventListener('mousedown', handler);
		onCleanup(() => document.removeEventListener('mousedown', handler));
	});

	const controlStyle = css(multiSelectStyle(), {
		cursor: 'pointer',
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: '0.25rem',
		minHeight: '2em',
		border: '1px solid rgb(222, 222, 222)',
		borderRadius: 3,
		background: 'white',
		padding: '0.3em 0.5em',
	});

	const placeholderStyle = css({
		color: 'rgb(150, 150, 150)',
	});

	const dropdownStyle = css({
		backgroundColor: 'white',
		borderRadius: '0.125rem',
		marginTop: '0.25rem',
		maxHeight: '50vh',
		width: 300,
		overflowY: 'auto',
		padding: '0.5rem 0',
		position: 'absolute',
		border: '1px solid rgb(222, 222, 222)',
		boxShadow: '0 10px 15px -3px rgb(0 0 0/0.1), 0 4px 6px -4px rgb(0 0 0/0.1)',
		zIndex: 100,
		fontSize: 12,
	});

	const categoryRowStyle = css({
		cursor: 'pointer',
		padding: '0.4rem 1rem',
		userSelect: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: '0.4em',
		fontWeight: 'bold',
		color: 'rgb(50, 50, 50)',
		'&:hover': { backgroundColor: 'rgb(243, 244, 246)' },
	});

	const categoryChevronStyle = css({
		marginLeft: 'auto',
		color: 'rgb(150, 150, 150)',
	});

	const backRowStyle = css(categoryRowStyle, {
		borderBottom: '1px solid rgb(229, 231, 235)',
	});

	const styleRowStyle = css({
		cursor: 'pointer',
		padding: '0.3rem 1rem',
		userSelect: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: '0.5em',
		'&:hover': { backgroundColor: 'rgb(229, 231, 235)' },
	});

	const styleBuilderLinkStyle = css({
		display: 'block',
		padding: '0.4rem 1rem',
		marginTop: '0.25rem',
		borderTop: '1px solid rgb(229, 231, 235)',
		color: 'rgb(37, 99, 235)',
		textDecoration: 'none',
		userSelect: 'none',
		'&:hover': { backgroundColor: 'rgb(243, 244, 246)' },
	});

	const explanationTooltipStyle = css({
		position: 'fixed',
		marginLeft: '0.5rem',
		backgroundColor: '#333',
		color: 'white',
		borderRadius: '0.25rem',
		padding: '0.4rem 0.6rem',
		fontSize: 11,
		lineHeight: 1.4,
		whiteSpace: 'normal',
		width: 200,
		zIndex: 200,
		pointerEvents: 'none',
		transform: 'translateY(-50%)',
	});

	const paramsBlockStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '1em',
		marginTop: '1em',
	});

	const paramsGroupStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5em',
	});

	/* Label on the left, the style's 'Apply to' picker on the right. Top-aligned so the picker's path
	   builder, when open, grows the row downwards and pushes the value editor below it rather than
	   drawing over it. */
	const labelRowStyle = css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '1em',
	});

	/* Where StyleFieldsRow portals the 'Apply to' picker. */
	const selectorSlotStyle = css({
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
	});

	const paramsInnerStyle = css({
		paddingLeft: '1em',
		borderLeft: 'solid 2px #ccc',
	});

	return (<>
		<div ref={containerRef!} style={{ position: 'relative' }}>
			<div class={controlStyle} onClick={() => isOpen() ? close() : setIsOpen(true)}>
				<Show when={value().length === 0}>
					<span class={placeholderStyle}>Click to add styles…</span>
				</Show>
				<DragDropProvider onDragEnd={onDragEnd} collisionDetector={closestCenter}>
					<DragDropSensors />
					<SortableProvider ids={value().map((_, i) => i + 1)}>
						<For each={value()}>{(entry, index) =>
							<SortablePill
								id={index() + 1}
								category={entry.category}
								type={entry.type}
								remove={e => removeStyle(entry.category, entry.type, e)}
							/>
						}</For>
					</SortableProvider>
				</DragDropProvider>
				<span style={{ 'margin-left': 'auto', 'padding-left': '0.25rem' }}>▾</span>
			</div>
			<Show when={isOpen()}>
				<div class={dropdownStyle}>
					<Show when={drilled()} fallback={
						<For each={pickableCategories(P.styleDefinitions)}>{cat =>
							<div class={categoryRowStyle} onClick={e => { e.stopPropagation(); setDrilled(cat); }}>
								<span>{categoryLabel(cat)}</span>
								<span class={categoryChevronStyle}>▸</span>
							</div>
						}</For>
					}>{cat => <>
						<div class={backRowStyle} onClick={e => { e.stopPropagation(); setDrilled(undefined); }}>
							<span>◂</span>
							<span>{categoryLabel(cat())}</span>
						</div>
						<For each={Object.keys(P.styleDefinitions[cat()] ?? {})}>{styleName =>
							<div
								class={styleRowStyle}
								onClick={e => toggleStyle(cat(), styleName, e)}
								onMouseEnter={e => {
									const explanation = P.styleDefinitions[cat()][styleName]?.explanation;
									if (!explanation) return;
									const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
									setTooltip({ category: cat(), name: styleName, top: rect.top + rect.height / 2, left: rect.right });
								}}
								onMouseLeave={() => setTooltip(null)}
							>
								<span>{isSelected(cat(), styleName) ? '☑' : '☐'}</span>
								<span>{styleName}</span>
							</div>
						}</For>
						<Show when={cat() === 'Venue styles'}>
							<a class={styleBuilderLinkStyle} href='/admin/style-builder' onClick={e => e.stopPropagation()}>
								Add or edit venue style
							</a>
						</Show>
					</>}</Show>
				</div>
			</Show>
			<Show when={tooltip()}>
				<div
					class={explanationTooltipStyle}
					style={{ top: `${tooltip()!.top}px`, left: `${tooltip()!.left}px` }}
				>
					{P.styleDefinitions[tooltip()!.category]?.[tooltip()!.name]?.explanation}
				</div>
			</Show>
		</div>

		{/* Parameter editor for each selected style that takes params (eg a colour picker for
		    'Custom > Background colour'). Rooted at the style's own params object in the store. */}
		<div class={paramsBlockStyle}>
			<For each={currentRaw()}>{(style, index) => {
				const slot: {node?: HTMLElement} = {};
				return (
					<Show when={P.styleDefinitions[style.category]?.[style.type]?.ui}>
						<div class={paramsGroupStyle}>
							<div class={labelRowStyle}>
								<label class={labelStyle()}>{categoryLabel(style.category)} {'>'} {style.type}</label>
								<div class={selectorSlotStyle} ref={el => { slot.node = el; }} />
							</div>
							<div class={paramsInnerStyle}>
								<StoreProvider {...sw} location={[...sw.location, field, index(), 'params']}>
									<SelectorSlot.Provider value={slot}>
										<Dynamic component={P.styleDefinitions[style.category][style.type].ui} />
									</SelectorSlot.Provider>
								</StoreProvider>
							</div>
						</div>
					</Show>
				);
			}}</For>
		</div>
	</>);
}

const tagStyle = css({
	backgroundColor: 'rgb(243, 244, 246)',
	borderRadius: '0.25rem',
	display: 'flex',
	alignItems: 'center',
	fontSize: '85%',
	paddingLeft: '2px',
	paddingRight: '2px',
	gap: '2px',
});

const removeBtn = css({
	cursor: 'pointer',
	padding: '0 2px',
	'&:hover': { opacity: 0.6 },
});

const gripStyle = css({
	cursor: 'grab',
	color: 'rgb(150, 150, 150)',
	touchAction: 'none',
	fontSize: '80%',
	padding: '0 3px',
	'&:hover': { color: 'rgb(90, 90, 90)' },
	'&:active': { cursor: 'grabbing' },
});

const draggingStyle = css({
	opacity: 0.4,
});

function SortablePill(P: { id: number, category: string, type: string, remove: (e: MouseEvent) => void })
{
	const sortable = createSortable(P.id);

	return (
		<span
			ref={sortable.ref}
			class={tagStyle}
			classList={{ [draggingStyle]: sortable.isActiveDraggable }}
			style={transformStyle(sortable.transform)}
		>
			<i class={`fas fa-ellipsis-v ${gripStyle}`} {...sortable.dragActivators} onClick={e => e.stopPropagation()} />
			<span>{categoryLabel(P.category)} {'>'} {P.type}</span>
			<span class={removeBtn} onClick={P.remove}>✕</span>
		</span>
	);
}

export const categoryLabel = (cat: string) => cat.charAt(0).toUpperCase() + cat.slice(1);

